import { useState, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Upload, Download, FileSpreadsheet, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";

interface ExcelImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entityType: string;
  entityLabel: string;
  onSuccess: () => void;
  templateDescription?: string;
  uploadDescription?: string;
}

function getFileNameFromDisposition(headerValue?: string) {
  if (!headerValue) return null;
  const utf8Match = headerValue.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) return decodeURIComponent(utf8Match[1]);
  const asciiMatch = headerValue.match(/filename="?([^";]+)"?/i);
  return asciiMatch?.[1] || null;
}

export function ExcelImportDialog({
  open,
  onOpenChange,
  entityType,
  entityLabel,
  onSuccess,
  templateDescription,
  uploadDescription,
}: ExcelImportDialogProps) {
  const { api } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState<{ success: boolean; message: string; count?: number; errors?: string[] } | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (selected) {
      if (!selected.name.match(/\.(xlsx|xls)$/i)) {
        toast.error("Please select an Excel file (.xlsx or .xls)");
        return;
      }
      setFile(selected);
      setResult(null);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const res = await api.get(`/excel/template/${entityType}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const fileName = getFileNameFromDisposition(res.headers["content-disposition"]) || `${entityType}_template.xlsx`;
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Sample template downloaded");
    } catch {
      toast.error("Failed to download template");
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error("Please select a file first");
      return;
    }
    setUploading(true);
    setResult(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await api.post(`/excel/upload/${entityType}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult({ success: true, message: res.data.message, count: res.data.count, errors: res.data.errors || [] });
      toast.success(res.data.message || `${res.data.count} ${entityLabel.toLowerCase()} imported successfully`);
      onSuccess();
    } catch (err: any) {
      const msg = err.response?.data?.detail || "Import failed. Please check the file format.";
      setResult({ success: false, message: msg });
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  const handleClose = (open: boolean) => {
    if (!uploading) {
      setFile(null);
      setResult(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      onOpenChange(open);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Import {entityLabel}</DialogTitle>
          <DialogDescription>
            Upload an Excel file to bulk-import {entityLabel.toLowerCase()}. Download the sample template to see the expected format.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Step 1: Download Template */}
          <div className="rounded-lg border border-dashed p-4 space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium">
              <span className="flex items-center justify-center w-5 h-5 rounded-full bg-primary text-white text-xs">1</span>
              Download sample template
            </div>
            <p className="text-xs text-muted-foreground ml-7">
              {templateDescription || `Get a pre-filled Excel template with sample data showing the correct format and required columns.`}
            </p>
            <Button variant="outline" size="sm" className="ml-7" onClick={handleDownloadTemplate}>
              <Download className="w-4 h-4 mr-1.5" /> Download Sample Excel
            </Button>
          </div>

          {/* Step 2: Upload File */}
          <div className="rounded-lg border border-dashed p-4 space-y-3">
            <div className="flex items-center gap-2 text-sm font-medium">
              <span className="flex items-center justify-center w-5 h-5 rounded-full bg-primary text-white text-xs">2</span>
              Upload your Excel file
            </div>
            <p className="text-xs text-muted-foreground ml-7">
              {uploadDescription || "Fill in the template with your data and upload it here. The first row should contain column headers."}
            </p>
            <div className="ml-7">
              <input ref={fileInputRef} type="file" accept=".xlsx,.xls" onChange={handleFileChange} className="hidden" id="excel-file-input" />
              <label
                htmlFor="excel-file-input"
                className="flex items-center gap-3 rounded-md border-2 border-dashed p-3 cursor-pointer hover:border-primary hover:bg-muted/30 transition-colors"
              >
                <FileSpreadsheet className="w-8 h-8 text-green-600 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  {file ? (
                    <>
                      <p className="text-sm font-medium truncate">{file.name}</p>
                      <p className="text-xs text-muted-foreground">{(file.size / 1024).toFixed(1)} KB</p>
                    </>
                  ) : (
                    <>
                      <p className="text-sm font-medium">Click to select file</p>
                      <p className="text-xs text-muted-foreground">Supports .xlsx and .xls files</p>
                    </>
                  )}
                </div>
              </label>
            </div>
          </div>

          {/* Result */}
          {result && (
            <div
              className={`flex items-start gap-2 rounded-md p-3 text-sm ${
                result.success ? "bg-green-50 text-green-800 border border-green-200" : "bg-red-50 text-red-800 border border-red-200"
              }`}
            >
              {result.success ? <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" /> : <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />}
              <div className="space-y-2">
                <span className="block">{result.message}</span>
                {result.errors && result.errors.length > 0 && (
                  <div className="rounded border border-current/20 bg-card/60 p-2">
                    <p className="text-xs font-medium">Skipped rows</p>
                    <ul className="mt-1 space-y-1 text-xs">
                      {result.errors.slice(0, 5).map((error) => (
                        <li key={error}>{error}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)} disabled={uploading}>
            {result?.success ? "Close" : "Cancel"}
          </Button>
          {!result?.success && (
            <Button className="bg-primary hover:bg-primary/90" onClick={handleUpload} disabled={!file || uploading}>
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Importing...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-1.5" /> Import
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
